import { Component, Input } from '@angular/core'

import { CobranzaMonthlySummary } from '../../data/interfaces/monthly-summary.interface'

/**
 * El embudo del mes: qué debería entrar, qué entró, qué se facturó y cuánto de eso ya se cobró.
 *
 * **UNA caja con cuatro casillas separadas por su borde**, no cuatro tarjetas flotantes. La lección ya
 * está escrita en `ledger-strip.component.html`: cuatro tarjetas con sombra se leen como cuatro
 * objetos y no como el estado de una cosa. Cobranza lo repetía dos veces —cinco en Comprobantes,
 * cuatro en «Por atender»— y esto lo cierra.
 *
 * Pertenece a la PANTALLA y no a una de sus vistas: dos de sus cifras son del dinero y dos de los
 * comprobantes, y el valor está en compararlas. Partirlas entre pestañas era lo que las hacía
 * inútiles.
 */
/** Las vistas que NO obedecen al selector de mes: pintan el libro completo o lo que trae un archivo. */
const SIN_MES = ['dinero', 'atender', 'conciliacion']

@Component({
  selector: 'app-cobranza-funnel',
  templateUrl: './cobranza-funnel.component.html',
  styleUrls: ['./cobranza-funnel.component.scss'],
})
export class CobranzaFunnelComponent {
  @Input() summary: CobranzaMonthlySummary | null = null
  @Input() loading = false

  /** Qué vista está abierta: se sombrea la casilla que esa vista detalla. */
  @Input() view: 'dinero' | 'comprobantes' | 'central' | 'cartera' | 'avance' | 'conciliacion' | 'atender' = 'dinero'

  /**
   * Si la vista de abajo obedece al selector de mes del encabezado.
   *
   *! Dinero y la cola de excepciones pintan el libro COMPLETO de pagos y no escuchan el selector;
   *! Comprobantes, CENTRAL, Cartera y Avance sí —los cuatro se suscriben a `shell.month$`—. El pie
   *! decía que ninguna lo hacía, y quien cambiaba el mes veía cambiar la tabla justo debajo de la
   *! frase que afirmaba lo contrario.
   *
   *! Conciliación tampoco: lo que enseña sale del ARCHIVO que se sube, no de un mes. Decirle que se
   *! filtra por el selector haría buscar un filtro que no existe.
   */
  get tablaSigueElMes(): boolean {
    return !SIN_MES.includes(this.view)
  }

  /**
   * Cuánto falta por entrar del mes. Se enseña dentro de «Entró» porque es la brecha entre las dos
   * primeras casillas, y leerla restando dos cifras a ojo es justo lo que la franja evita.
   */
  get gap(): number {
    if (!this.summary) return 0

    return Math.max(0, this.summary.expected.amount - this.summary.received.amount)
  }

  /** Se cobró de más: pagos anticipados, o de meses anteriores. No es un error, y se dice distinto. */
  get overpaid(): number {
    if (!this.summary) return 0

    return Math.max(0, this.summary.received.amount - this.summary.expected.amount)
  }

  /**
   * Qué parte de lo facturado ya cerró su ciclo, en porcentaje.
   *
   * La cuarta casilla es un SUBCONJUNTO de la tercera, así que lleva medidor y su «de $X»: como
   * cuarto número suelto invitaría a sumarla con las otras tres, y esa suma no significa nada.
   */
  get collectedPct(): number {
    const total = this.summary?.invoiced.amount ?? 0
    const cobrado = this.summary?.invoicedCollected.amount ?? 0
    if (total <= 0) return 0

    return Math.min(100, Math.round((cobrado / total) * 100))
  }

  /**
   * El mes al que pertenecen las cuatro cifras, escrito.
   *
   * Va aquí y no solo en el selector del encabezado porque el selector, a dos dedos del título de
   * la pantalla, se lee como si filtrara TODO — y no: las tablas son el libro completo. Con el mes
   * escrito sobre la franja queda dicho qué gobierna. No se le baja a las tablas a propósito: un
   * filtro de fecha empuja el listado a la estrategia «precisa», que corta a 15 000 documentos y
   * devuelve el total etiquetado como exacto (B-11).
   */
  get monthLabel(): string {
    const [y, m] = (this.summary?.month ?? '').split('-').map(Number)
    if (!y || !m) return ''
    const nombre = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'][m - 1]

    return nombre ? `${nombre} ${y}` : ''
  }

  /** El sombreado marca la casilla que la vista abierta detalla. */
  isActive(tile: 'expected' | 'received' | 'invoiced' | 'collected'): boolean {
    //* CENTRAL es un libro de FACTURAS, igual que Comprobantes: la cifra que la vista está mirando
    //* es la facturada, no la que entró.
    if (this.view === 'comprobantes' || this.view === 'central') return tile === 'invoiced'

    return tile === 'received'
  }
}
